import { useInfiniteApiQuery } from '@tasama/api-client-query';
import { QUERY_CACHE_TIME } from '@/config/query-config';
import type { Service } from '../types';

/**
 * My Services Infinite Query Response
 * Response structure from the services API with pagination
 */
export interface MyServicesResponse {
  services: Service[];
  pendingTasksCount: number;
  totalElements: number;
  page: number;
  size: number;
  totalPages: number;
}

interface UseInfiniteMyServicesQueryParams {
  status?: 'REQUESTED' | 'ACTIVE' | 'COMPLETED';
  size?: number;
  enabled?: boolean;
  /** TDF-1350: optional L1/L2/L3 cascading filters, applied server side over the full tab. */
  l1Id?: string | undefined;
  l2Id?: string | undefined;
  l3Id?: string | undefined;
}

/**
 * Hook for fetching my services with infinite scroll support
 * Uses React Query's useInfiniteQuery for automatic pagination
 *
 * @param status - Service status filter (REQUESTED, ACTIVE, COMPLETED)
 * @param size - Number of services to fetch per page (default: 20)
 * @param enabled - Whether to enable the query (default: true)
 * @param l1Id - Optional L1 category filter (TDF-1350)
 * @param l2Id - Optional L2 subcategory filter, narrowed to l1Id (TDF-1350)
 * @param l3Id - Optional L3 service filter, narrowed to l2Id (TDF-1350)
 * @returns React Query infinite query result
 */
export const useInfiniteMyServicesQuery = ({
  status = 'REQUESTED',
  size = 20,
  enabled = true,
  l1Id,
  l2Id,
  l3Id,
}: UseInfiniteMyServicesQueryParams = {}) => {
  return useInfiniteApiQuery<MyServicesResponse, MyServicesResponse>(
    ['services', 'list', status, l1Id ?? '', l2Id ?? '', l3Id ?? ''],
    {
      getEndpoint: (pageParam: number) => {
        const queryParams = new URLSearchParams({
          status,
          page: pageParam.toString(),
          size: size.toString(),
        });
        if (l1Id) queryParams.set('l1Id', l1Id);
        if (l2Id) queryParams.set('l2Id', l2Id);
        if (l3Id) queryParams.set('l3Id', l3Id);
        return `/services/service-request/list?${queryParams.toString()}`;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        // Calculate the next page number
        const currentPage = lastPage.page;
        const totalPages = lastPage.totalPages;

        // If we have more pages, return the next page number
        const hasMore = currentPage < totalPages - 1;
        return hasMore ? currentPage + 1 : undefined;
      },
      staleTime: QUERY_CACHE_TIME.STALE_TIME,
      retry: 3,
      retryDelay: 1000,
      enabled,
    }
  );
};
